/**
 * Roles and permissions, after Builder's (`SecurityRoles`, `PERMISSIONS`, `ROLE_PERMISSION_MAPPING`).
 * The database holds them (`roles`, `permissions`, `role_permissions`) and is the authority at
 * runtime: the Roles page edits grants live. This file names them and gives each role its default
 * grants, which `ensureRbac` applies once, when it creates the role.
 *
 * No imports, so the web app reads it through the `solution-advisor-application-services/rbac` export.
 */

/** Builder's nine roles, with Builder's names, plus Solution Advisor's prospect. */
export const SECURITY_ROLES = {
	systemAdmin: "systemAdmin",
	dlrAdmin: "dlrAdmin",
	engineeringAdmin: "engineeringAdmin",
	solutionsArchitect: "solutionsArchitect",
	solutionsEngineer: "solutionsEngineer",
	designEngineer: "designEngineer",
	sales: "sales",
	costEstimator: "costEstimator",
	customer: "customer",
	/** Ours, not Builder's: someone who signed up and is not a customer yet. Customer's grants by default. */
	prospect: "prospect"
} as const;

export type SecurityRole = (typeof SECURITY_ROLES)[keyof typeof SECURITY_ROLES];

export const SECURITY_ROLE_DESCRIPTIONS: Record<SecurityRole, string> = {
	systemAdmin: "Full system administrators",
	dlrAdmin: "DLR internal administrators",
	engineeringAdmin: "Engineering Administrators",
	solutionsArchitect: "Solutions Architects",
	solutionsEngineer: "Solutions Engineers",
	designEngineer: "Design Engineers",
	sales: "Sales",
	costEstimator: "Cost Estimators — read-only access",
	customer: "Customers, working in their own organization",
	prospect: "Prospects who signed up, working in their own organization until they become customers"
};

/**
 * Builder's global admins. Holding either in any organization lets a person enter every
 * organization and act there with that role (systemAdmin wins if they hold both). A systemAdmin
 * passes every permission check, whatever its grants say, as in Builder.
 */
export const GLOBAL_ADMIN_ROLES: readonly SecurityRole[] = [SECURITY_ROLES.systemAdmin, SECURITY_ROLES.dlrAdmin];

/**
 * The role a person gets in an organization they create, including their first-sign-in sandbox: a
 * prospect, until staff make them a customer on the Members page.
 */
export const DEFAULT_ORGANIZATION_ROLE: SecurityRole = SECURITY_ROLES.prospect;

/**
 * Builder's permission naming (`companyReaders`, `securityRoleEditors`) written `<area>:<level>`.
 * Areas are Solution Advisor's own; there are no build, product or device permissions here.
 */
export const PERMISSIONS = {
	allOrganizationReaders: "allOrganization:readers",
	myOrganizationReaders: "myOrganization:readers",
	myOrganizationEditors: "myOrganization:editors",

	companyReaders: "company:readers",
	companyEditors: "company:editors",
	projectReaders: "project:readers",
	projectEditors: "project:editors",
	solutionReaders: "solution:readers",
	solutionEditors: "solution:editors",
	briefcaseReaders: "briefcase:readers",
	briefcaseEditors: "briefcase:editors",

	advisorUsers: "advisor:users",

	userReaders: "user:readers",
	userEditors: "user:editors",

	securityRoleReaders: "securityRole:readers",
	securityRoleEditors: "securityRole:editors",

	apiTokenUsers: "apiToken:users",
	apiTokenAdministrators: "apiToken:administrators",

	locationCatalogEditors: "locationCatalog:editors",
	marketingResourceEditors: "marketingResource:editors",
	featureFlagEditors: "featureFlag:editors"
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
	"allOrganization:readers": "See every organization and enter any of them (Platform admin)",
	"myOrganization:readers":
		"Digital Realty staff access to the organization; customers lack it, so cannot hand out staff roles",
	"myOrganization:editors": "Rename the organization",
	"company:readers": "Read companies and their insights",
	"company:editors": "Create, edit and delete companies; refresh insights",
	"project:readers": "Read projects, their context and locations",
	"project:editors": "Create, edit and delete projects, their context and locations",
	"solution:readers": "Read solutions, models, builds and workspaces",
	"solution:editors": "Create, edit and delete solutions, models and builds; generate briefs",
	"briefcase:readers": "Read and download briefcase documents",
	"briefcase:editors": "Upload, add and delete briefcase documents",
	"advisor:users":
		"Chat with the AI advisor and read your conversations (also needs company, project, solution and briefcase readers)",
	"user:readers": "See the organization's members",
	"user:editors": "Add and remove members and change their roles",
	"securityRole:readers": "See roles and their permissions",
	"securityRole:editors": "Grant and revoke role permissions",
	"apiToken:users": "Create and revoke your own API tokens",
	"apiToken:administrators": "See and revoke every API token in the organization",
	"locationCatalog:editors": "Refresh the location catalog (metros and data centers) from DXM",
	"marketingResource:editors": "Create, edit and delete the marketing resource library every organization shares",
	"featureFlag:editors": "Create, switch and archive feature flags, and set them per organization"
};

const S = SECURITY_ROLES;
const ALL: readonly SecurityRole[] = Object.values(S);
/** Builder's "internal" set: everyone but customers (and prospects). */
const INTERNAL: readonly SecurityRole[] = ALL.filter(role => role !== S.customer && role !== S.prospect);
const ADMINS: readonly SecurityRole[] = [S.systemAdmin, S.dlrAdmin];
/** Builder's company/project editors, plus customers and prospects, who design their own solutions here. */
const CONTENT_EDITORS: readonly SecurityRole[] = [
	S.systemAdmin,
	S.dlrAdmin,
	S.engineeringAdmin,
	S.solutionsArchitect,
	S.solutionsEngineer,
	S.designEngineer,
	S.sales,
	S.customer,
	S.prospect
];
/** Builder's build editors (sales and cost estimators do not design), plus customers and prospects. */
const SOLUTION_EDITORS: readonly SecurityRole[] = [
	S.systemAdmin,
	S.dlrAdmin,
	S.engineeringAdmin,
	S.solutionsArchitect,
	S.solutionsEngineer,
	S.designEngineer,
	S.customer,
	S.prospect
];
/** Customers and prospects own their organization: they name it and invite colleagues, as admins do. */
const ORGANIZATION_MANAGERS: readonly SecurityRole[] = [...ADMINS, S.customer, S.prospect];

/**
 * What using the AI advisor (a chat turn, the conversation history) takes. Its answers draw on the
 * turn's scope, on what the conversation was grounded in before (stored messages and the AI infra's
 * memory thread), and on the caller's activity across every area, so per-record checks cannot bound
 * what it may repeat. Instead the advisor is only for callers who can read every content area it draws
 * on. The API (`canUseAdvisor`) and the web app (`usePermissions().canUseAdvisor`) both use this list.
 */
export const ADVISOR_PERMISSIONS: readonly Permission[] = [
	PERMISSIONS.advisorUsers,
	PERMISSIONS.companyReaders,
	PERMISSIONS.projectReaders,
	PERMISSIONS.solutionReaders,
	PERMISSIONS.briefcaseReaders
];

/**
 * Default grants (Builder's ROLE_PERMISSION_MAPPING). Applied to a role only when `ensureRbac`
 * creates it, so grants changed on the Roles page survive deploys; a new default for an existing
 * role needs a backfill migration, as in Builder.
 */
export const ROLE_PERMISSION_MAPPING: ReadonlyArray<{ permission: Permission; roles: readonly SecurityRole[] }> = [
	{ permission: PERMISSIONS.allOrganizationReaders, roles: ADMINS },
	{ permission: PERMISSIONS.myOrganizationReaders, roles: INTERNAL },
	{ permission: PERMISSIONS.myOrganizationEditors, roles: ORGANIZATION_MANAGERS },
	{ permission: PERMISSIONS.companyReaders, roles: ALL },
	{ permission: PERMISSIONS.companyEditors, roles: CONTENT_EDITORS },
	{ permission: PERMISSIONS.projectReaders, roles: ALL },
	{ permission: PERMISSIONS.projectEditors, roles: CONTENT_EDITORS },
	{ permission: PERMISSIONS.solutionReaders, roles: ALL },
	{ permission: PERMISSIONS.solutionEditors, roles: SOLUTION_EDITORS },
	{ permission: PERMISSIONS.briefcaseReaders, roles: ALL },
	{ permission: PERMISSIONS.briefcaseEditors, roles: CONTENT_EDITORS },
	{ permission: PERMISSIONS.advisorUsers, roles: ALL },
	{ permission: PERMISSIONS.userReaders, roles: ALL },
	{ permission: PERMISSIONS.userEditors, roles: ORGANIZATION_MANAGERS },
	{ permission: PERMISSIONS.securityRoleReaders, roles: ADMINS },
	{ permission: PERMISSIONS.securityRoleEditors, roles: [S.systemAdmin] },
	// API tokens (the CLIs and MCP server) are a systemAdmin tool; grant them wider on the Roles page.
	{ permission: PERMISSIONS.apiTokenUsers, roles: [S.systemAdmin] },
	{ permission: PERMISSIONS.apiTokenAdministrators, roles: [S.systemAdmin] },
	{ permission: PERMISSIONS.locationCatalogEditors, roles: ADMINS },
	{ permission: PERMISSIONS.marketingResourceEditors, roles: ADMINS },
	// As in Builder: a systemAdmin tool. A flag switches a feature for every organization at once.
	{ permission: PERMISSIONS.featureFlagEditors, roles: [S.systemAdmin] }
];
